import { upperCase } from "@/utils/upperCase";
import { fetchApproveTNA, fetchDataByTab, fetchTNAPeriod, fetchSubordinatesLearning, fetchSummaryData, tabToLearningType } from "../../api";
import { emp, getBadgeStatus, handleApproveSwal, handleApproveWithReviseSwal, handleRejectSwal, msgActionSwal, summaryData } from "../../settings";
import { formatCurrency } from "@/utils/formatCurrency";
import { formatDateShort } from "@/utils/dateFormatter";
import { getApprovalStatusLabel, getNextApproverLabel, isApprovalActionable } from "@/utils/approvalStatus";

const normalizeEmployee = (employee = {}, fallbackNik = "") => ({
    nik: employee.nik || fallbackNik || "",
    name: employee.name || employee.fullname || employee.full_name || "-",
    division: employee.division || employee.department || "-",
    position: employee.position || employee.position_name || employee.position_level || "-",
});

export default {
    data() {
        const currentYear = new Date().getFullYear();
        return {
            optionsSelectPeriod: [],
            selectedPeriod: null,
            emp: emp,
            isLoading: false,
            isLoadingEmp: false,
            isLoadingSummary: false,
            searchQuery: "",
            activeTab: "structured",
            payload: {
                learning_type: 'structured',
                nik: this.$route.params.nik ?? '',
                all: true,
                approvalOnly: false,
                reviewDetail: true,
                // search: '',
                // page: 1,
                // pageSize: 10,
            },
            totalItems: 0,
            data: [],
            yearColumns: Array.from({ length: 5 }, (_, index) => currentYear + index),
            kanbanColumns: [],
            summaryData: summaryData,
            selectedItems: [],
            showDetailTrainingModal: false,
            selectedTraining: null,
            isLoadingDetailTraining: false,
        };
    },
    watch: {
        selectedPeriod: {
            handler(newVal, oldVal) {
                if (!newVal || newVal === oldVal) {
                    return;
                }

                this.payload = {
                    ...this.payload,
                    tna_id: newVal,
                    learning_type: tabToLearningType[this.activeTab],
                };

                this.selectedItems = [];
                this.fetchDataByTab(this.buildRequestPayload());
                this.fetchSummaryData(this.buildRequestPayload());
            },
            immediate: false
        },
        data: {
            handler(newData) {
                this.kanbanColumns = this.buildKanbanColumns(newData || []);
                const validIds = new Set((newData || []).map(item => item.id));
                this.selectedItems = this.selectedItems.filter(item => validIds.has(item.id));
            },
            immediate: true,
        },
        searchQuery() {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.runSearch();
            }, 500);
        },
    },
    computed: {
        aboutMe() {
            return this.$store.getters["users/profiles"];
        },
        currentUserRoles() {
            const profile = this.$store.getters["users/profileData"] || this.aboutMe || {};
            return profile?.roles_uenroll || profile?.roles || [];
        },
    },
    methods: {
        upperCase,
        formatCurrency,
        getBadgeStatus,
        getApprovalStatusLabel,
        getNextApproverLabel,
        buildKanbanColumns(items = []) {
            const defaultYear = this.yearColumns[0];
            const grouped = this.yearColumns.reduce((acc, year) => {
                acc[year] = [];
                return acc;
            }, {});

            (items || []).forEach((item) => {
                const mappedYear = Number(item?.idp_period?.year);
                const targetYear = grouped[mappedYear] ? mappedYear : defaultYear;
                grouped[targetYear].push({ 
                    ...item, 
                    idp_period: { 
                        ...item.idp_period, 
                        year: targetYear 
                    } 
                });
            });

            return this.yearColumns.map((year) => ({
                year,
                items: grouped[year],
            }));
        },
        isItemChecked(item) {
            return this.selectedItems.some(selected => selected.id === item.id);
        },
        isItemSelectable(item) {
            return isApprovalActionable(item, this.currentUserRoles);
        },
        isMandatory(item) {
            const directValue =
                item?.mandatory ??
                item?.is_mandatory ??
                item?.remarks ??
                item?.training_mapping?.is_mandatory ??
                item?.training_mapping?.mandatory ??
                item?.training?.training_mappings?.[0]?.mandatory ??
                item?.training?.training_mappings?.[0]?.is_mandatory;

            if (typeof directValue === "boolean") return directValue;
            if (typeof directValue === "number") return directValue === 1;
            if (typeof directValue === "string") {
                const normalized = directValue.trim().toLowerCase();
                if (["true", "1", "yes", "y", "mandatory"].includes(normalized)) return true;
                if (["false", "0", "no", "n", "development", "gap", "gap_competencies"].includes(normalized)) return false;
            }

            return false;
        },
        getCardTitle(item) {
            return item?.training?.title || item?.coaching?.title || "Untitled Training";
        },
        getLearningTypeLabel(item) {
            return this.isMandatory(item) ? "Mandatory" : "Development";
        },
        getLearningTypeClass(item) {
            return this.isMandatory(item) ? "mandatory-label" : "dev-label text-info";
        },
        getSelectedTrainingPlan(items) {
            const sourceItems = Array.isArray(items) ? 
                                  items : 
                                    items ? [items] : [];
            const selectedIds = new Set(sourceItems.map(item => item.id));

            const mappedPlan = (this.kanbanColumns || [])
                .flatMap((column) => {
                    const year = Number(column?.year) || null;
                    const trainings = column?.items || [];

                    return trainings
                        .filter((training) => selectedIds.has(training.id))
                        .map((training) => ({
                            tna_id: training.id,
                            training_mapping_id: training.training_mapping_id || null,
                            year,
                        }));
                });

            // Fallback when item is not found inside kanbanColumns yet.
            if (mappedPlan.length < selectedIds.size) {
                const mappedIds = new Set(mappedPlan.map(item => item.training_id));
                sourceItems
                    .filter(item => !mappedIds.has(item.id))
                    .forEach((item) => {
                        mappedPlan.push({
                            training_id: item.id,
                            training_mapping_id: item.training_mapping_id || null,
                            year: Number(item?.idp_period?.year) || null,
                        });
                    });
            }

            return mappedPlan;
        },
        handleKanbanDragEnd() {
            this.kanbanColumns.forEach((column) => {
                column.items.forEach((item) => {
                    if (!item.idp_period) {
                        item.idp_period = {};
                    }
                    item.idp_period.year = column.year;
                });
            });
        },
        handleTabChange(tab) {
            this.activeTab = tab;
            this.payload.learning_type = tabToLearningType[this.activeTab];
            this.selectedItems = [];
            this.fetchDataByTab(this.buildRequestPayload());
            this.fetchSummaryData(this.buildRequestPayload());
        },
        handlePageChange(page) {
            this.payload.page = page;
            this.fetchDataByTab(this.buildRequestPayload());
        },
        runSearch() {
            this.payload.search = (this.searchQuery || "").trim();
            this.fetchDataByTab(this.buildRequestPayload());
        },
        reloadFilteredData() {
            if (!this.payload.tna_id && !this.selectedPeriod) return;
            this.selectedItems = [];
            const payload = this.buildRequestPayload();
            this.fetchDataByTab(payload);
            this.fetchSummaryData(payload);
        },
        buildRequestPayload() {
            return { ...this.payload };
        },
        openSubmitModal() {
            this.$bvModal.show("modal-confirmation");
        },
        openRejectModal() {
            this.$bvModal.show("modal-reject-confirmation");
        },
        openTrainingDetail(item) {
            const training = item?.training || item?.coaching || {};
            this.selectedTraining = {
                ...training,
                ...item,
                title: training.title || item?.title,
                status: item?.status,
                kplh_status: training.kplh_status || item?.kplh_status,
            };
            this.showDetailTrainingModal = true;
        },
        closeTrainingDetailModal() {
            this.showDetailTrainingModal = false;
            this.selectedTraining = null;
        },
        toggleSelect(item) {
            const exists = this.selectedItems.find(i => i.id === item.id);
            if (exists) {
                this.selectedItems = this.selectedItems.filter(i => i.id !== item.id);
            } else {
                this.selectedItems.push(item);
            }
        },
        async fetchDataByTab(payload) {
            try {
                this.isLoading = true;
                const response = await fetchDataByTab(payload);

                this.data = response;
                // this.payload.page = response.page;
                // this.payload.perPage = response.limit;
                this.totalItems = response.totalItems;
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                this.isLoading = false;
            }
        },
        async fetchSubordinatesLearning() {
            try {
                this.isLoadingEmp = true;
                const nik = this.payload.nik || this.$route.params.nik || "";
                const response = await fetchSubordinatesLearning({
                    nik,
                    all: false,
                    approvalOnly: false,
                    hasLearningOnly: false,
                    countApprovalOnly: true,
                    learning_type: tabToLearningType[this.activeTab],
                    page: 1,
                    pageSize: 1,
                });
                const rows = Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response)
                        ? response
                        : [];
                const employee = rows.find(item => String(item.nik) === String(nik)) || rows[0] || {};
                this.emp = normalizeEmployee(employee, nik);
            } catch (error) {
                console.error("Error fetching subordinates learning:", error);
                this.emp = normalizeEmployee({}, this.payload.nik || this.$route.params.nik);
            } finally {
                this.isLoadingEmp = false;
            }
        },
        async fetchSummaryData(payload) {
            try {
                this.isLoadingSummary = true;
                const response = await fetchSummaryData(payload);
                this.summaryData = response || {};
            } catch (error) {
                console.error("Error fetching summary data:", error);
            } finally {
                this.isLoadingSummary = false;
            }
        },
        async handleSubmit() {
            if (this.selectedItems.length === 0) {
                this.$swal('Error', 'Tidak ada item yang dipilih!', 'error');
                return;
            }
            try {
                this.$swal({
                    title: 'Menyetujui...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });
                const selectedTrainingPlan = this.getSelectedTrainingPlan(this.selectedItems);
                const payload = {
                    learning_type: tabToLearningType[this.activeTab],
                    nik: this.payload.nik,
                    ids: selectedTrainingPlan,
                    action: 'approve' // tambahan parameter action
                };
                const res = await fetchApproveTNA(payload);
                this.$swal.close();
                this.$swal({
                    icon: 'success',
                    title: 'Disetujui',
                    text: 'Berhasil approve TNA.',
                    timer: 2000
                });
                this.$bvModal.hide("modal-confirmation");
                this.selectedItems = [];
                this.fetchDataByTab(this.buildRequestPayload());
                this.fetchSummaryData(this.buildRequestPayload());
            } catch (error) {
                this.$swal.close();
                console.error(error);
                this.$swal({
                    icon: 'error',
                    title: 'Error!',
                    text: 'Approve gagal, coba lagi.'
                });
            }
        },
        async handleRejectSelected() {
            if (this.selectedItems.length === 0) {
                this.$swal('Error', 'Tidak ada item yang dipilih!', 'error');
                return;
            }
            try {
                this.$swal({
                    title: 'Menolak...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });
                const selectedTrainingPlan = this.getSelectedTrainingPlan(this.selectedItems);
                const payload = {
                    learning_type: tabToLearningType[this.activeTab],
                    nik: this.payload.nik,
                    ids: selectedTrainingPlan,
                    action: 'reject' // tambahan parameter action
                };
                const res = await fetchApproveTNA(payload);
                this.$swal.close();
                this.$swal({
                    icon: 'success',
                    title: 'Rejected!',
                    text: 'Berhasil reject TNA.',
                    timer: 2000
                });
                this.$bvModal.hide("modal-reject-confirmation");
                this.selectedItems = [];
                this.fetchDataByTab(this.buildRequestPayload());
                this.fetchSummaryData(this.buildRequestPayload());
            } catch (error) {
                this.$swal.close();
                console.error(error);
                this.$swal({
                    icon: 'error',
                    title: 'Error!',
                    text: 'Reject gagal, coba lagi.'
                });
            }
        },
        async fetchTNAPeriod() {
            try {
                const res = await fetchTNAPeriod();
                this.optionsSelectPeriod = res.data?.map(item => ({
                    text: `${formatDateShort(item.start_date)} - ${formatDateShort(item.end_date)}`,
                    value: item.id
                })) || [];

                // Auto-select period: pilih yang tanggal sekarang berada di antara start_date dan end_date
                if (this.optionsSelectPeriod.length > 0) {
                    await this.$nextTick();
                    const now = new Date();
                    const activePeriod = (res.data || []).find(item => new Date(item.start_date) <= now && new Date(item.end_date) >= now);
                    this.selectedPeriod = activePeriod?.id || null;
                }
            } catch (error) {
                console.error('Error fetching IDP periods:', error);
            }
        },
        async handleRevise(item) {
            const { value: notes } = await this.$swal(handleApproveWithReviseSwal);
            if (notes !== undefined) {
                try {
                    this.$swal({
                        title: 'Menyetujui dengan umpan balik...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            this.$swal.showLoading();
                        }
                    });
                    const payload = {
                        learning_type: tabToLearningType[this.activeTab],
                        action: 'approve',
                        nik: this.payload.nik,
                        ids: this.getSelectedTrainingPlan(item),
                        notes: notes
                    };
                    await fetchApproveTNA(payload);
                    this.$swal.close();
                    this.$swal(msgActionSwal['success']);
                    this.fetchDataByTab(this.buildRequestPayload());
                    this.fetchSummaryData(this.buildRequestPayload());
                } catch (error) {
                    this.$swal.close();
                    console.error(error);
                    this.$swal(msgActionSwal['error']);
                }
            }
        },
        async handleApprove(item) {
            const result = await this.$swal(handleApproveSwal);
            if (!result?.isConfirmed) {
                return;
            }
            try {
                this.$swal({
                    title: 'Menyetujui...',
                    allowOutsideClick: false,
                    didOpen: () => {
                        this.$swal.showLoading();
                    }
                });
                const payload = {
                    learning_type: tabToLearningType[this.activeTab],
                    action: 'approve',
                    nik: this.payload.nik,
                    ids: this.getSelectedTrainingPlan(item),
                };
                await fetchApproveTNA(payload);
                this.$swal.close();
                this.$swal(msgActionSwal['success']);
                this.fetchDataByTab(this.buildRequestPayload());
                this.fetchSummaryData(this.buildRequestPayload());
            } catch (error) {
                this.$swal.close();
                console.error(error);
                this.$swal(msgActionSwal['error']);
            }
        },
        async handleReject(item) {
            const { value: notes } = await this.$swal(handleRejectSwal);
            if (notes !== undefined) {
                try {
                    this.$swal({
                        title: 'Menolak...',
                        allowOutsideClick: false,
                        didOpen: () => {
                            this.$swal.showLoading();
                        }
                    });
                    const payload = {
                        learning_type: tabToLearningType[this.activeTab],
                        action: 'reject',
                        nik: this.payload.nik,
                        ids: this.getSelectedTrainingPlan(item),
                        notes: notes
                    };
                    await fetchApproveTNA(payload);
                    this.$swal.close();
                    this.$swal(msgActionSwal['success']);
                    this.fetchDataByTab(this.buildRequestPayload());
                    this.fetchSummaryData(this.buildRequestPayload());
                } catch (error) {
                    this.$swal.close();
                    console.error(error);
                    this.$swal(msgActionSwal['error']);
                }
            }
        }
    },
    mounted() {
        const nik = this.$route.params.nik;
        if (nik) {
            this.payload.nik = nik;
            this.payload.learning_type = tabToLearningType[this.activeTab];
            this.fetchTNAPeriod();
            this.fetchDataByTab(this.buildRequestPayload());
            this.fetchSubordinatesLearning();
            this.fetchSummaryData(this.buildRequestPayload());
        }
    }
}
